// src/pages/HistoryPage.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { SleevePicker, fmtMoney, fmtPct, fmtNum, colorClass } from '../components';

const fmt = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtTime = d => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

export default function HistoryPage() {
  const { isOwner } = useAuth();
  const [sleeves, setSleeves] = useState([]);
  const [trades, setTrades] = useState([]);           // from position_trades (entries + exits)
  const [openPositions, setOpenPositions] = useState([]); // currently open unrealized
  const [activeSleeve, setActiveSleeve] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');         // 'all' | 'entry' | 'exit' | 'open'
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // { row }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadTrades(); }, [activeSleeve]);

  async function loadData() {
    const { data: s } = await supabase.from('sleeves').select('*').order('name');
    setSleeves(s || []);
    await loadTrades();
    setLoading(false);
  }

  async function loadTrades() {
    // 1. Completed trade records (entries + exits)
    let q = supabase
      .from('position_trades')
      .select('*, sleeves(name)')
      .order('traded_at', { ascending: false });
    if (activeSleeve) q = q.eq('sleeve_id', activeSleeve);
    const { data: tradeData } = await q;
    setTrades(tradeData || []);

    // 2. Currently open positions (unrealized) — shown as open entries
    let qOpen = supabase
      .from('positions')
      .select('*, securities(*), sleeves(name)')
      .eq('is_active', true);
    if (activeSleeve) qOpen = qOpen.eq('sleeve_id', activeSleeve);
    const { data: openData } = await qOpen;
    setOpenPositions(openData || []);
  }

  async function handleDelete() {
    if (!isOwner) return;
    if (!deleteTarget?.row) return;
    const row = deleteTarget.row;
    setDeleting(true);

    try {
      // 1) Virtual open position row (not in position_trades)
      if (row._type === 'open_position') {
        // Wipe all trades (if any) + remove from portfolio displays
        await supabase.from('position_trades').delete().eq('position_id', row.position_id);
        await supabase.from('positions').update({ is_active: false }).eq('id', row.position_id);
      }

      // 2) Stored trade row
      if (row._type === 'trade') {
        // If deleting an EXIT, also delete the matching ENTRY portion.
        // If the exit is a partial close, reduce the entry quantity by the exit quantity.
        if (row.trade_type === 'exit' && row.position_id) {
          // Find entry trade(s) for this position
          const { data: entryTrades } = await supabase
            .from('position_trades')
            .select('*')
            .eq('position_id', row.position_id)
            .eq('trade_type', 'entry')
            .order('traded_at', { ascending: true })
            .limit(1);

          const entry = entryTrades?.[0];
          const exitQty = Number(row.quantity || 0);
          const entryQty = Number(entry?.quantity || 0);

          // Delete the exit trade row
          await supabase.from('position_trades').delete().eq('id', row.id);

          if (entry && exitQty > 0 && entryQty > 0) {
            const remaining = entryQty - exitQty;
            if (remaining > 0) {
              await supabase.from('position_trades').update({
                quantity: remaining,
                trade_value: Math.abs(remaining * Number(entry.price || 0)),
              }).eq('id', entry.id);
            } else {
              // Exit covered full entry quantity: remove the entry record too
              await supabase.from('position_trades').delete().eq('id', entry.id);
              // Also remove the position from active displays (position is already inactive on full exits)
              await supabase.from('positions').update({ is_active: false }).eq('id', row.position_id);
            }
          }
        } else if (row.trade_type === 'entry' && row.position_id) {
          // Deleting an ENTRY wipes all exits for that position and removes position from portfolio displays
          await supabase.from('position_trades').delete().eq('position_id', row.position_id);
          await supabase.from('positions').update({ is_active: false }).eq('id', row.position_id);
        } else {
          // Fallback: delete only this trade record
          await supabase.from('position_trades').delete().eq('id', row.id);
        }
      }
    } finally {
      setDeleteTarget(null);
      setDeleting(false);
      loadTrades();
    }
  }

  // Merge open positions into a unified display list
  // Open positions that already have an entry trade won't appear twice because
  // we only show them in the "open" virtual row if they have no matching trade entry
  const openPositionIds = new Set((trades.filter(t => t.trade_type === 'entry')).map(t => t.position_id).filter(Boolean));

  const openRows = openPositions
    .filter(p => !openPositionIds.has(p.id))  // only show if no logged entry trade
    .map(p => ({
      _type: 'open_position',
      id: `open-${p.id}`,
      position_id: p.id,
      trade_type: 'entry',
      ticker: p.securities?.ticker,
      security_name: p.securities?.name,
      cusip: p.cusip,
      sleeve_id: p.sleeve_id,
      sleeves: p.sleeves,
      direction: p.direction,
      quantity: p.quantity,
      price: p.entry_price,
      trade_value: Math.abs(p.quantity * p.entry_price),
      entry_price: null,
      realized_pnl: null,
      realized_pnl_pct: null,
      unrealized_pnl: p.unrealized_pnl,
      unrealized_pnl_pct: p.unrealized_pnl_pct,
      current_price: p.current_price,
      notes: p.notes,
      traded_at: p.created_at,
      is_open: true,
    }));

  // All rows for display: completed trades + open positions without logged entry
  const allRows = [
    ...trades.map(t => ({ ...t, _type: 'trade' })),
    ...openRows,
  ].sort((a, b) => new Date(b.traded_at) - new Date(a.traded_at));

  const filtered = allRows.filter(t => {
    if (filter === 'open' && !t.is_open) return false;
    if (filter === 'entry' && t.trade_type !== 'entry') return false;
    if (filter === 'exit' && t.trade_type !== 'exit') return false;
    if (search) {
      const s = search.toLowerCase();
      return (t.ticker || '').toLowerCase().includes(s) ||
             (t.cusip || '').toLowerCase().includes(s) ||
             (t.security_name || '').toLowerCase().includes(s);
    }
    return true;
  });

  // Summary stats
  const exits = trades.filter(t => t.trade_type === 'exit');
  const totalRealizedPnl = exits.reduce((s, t) => s + (t.realized_pnl || 0), 0);
  const winCount = exits.filter(t => (t.realized_pnl || 0) > 0).length;
  const winRate = exits.length > 0 ? (winCount / exits.length) * 100 : null;
  const avgReturn = exits.length > 0
    ? exits.reduce((s, t) => s + (t.realized_pnl_pct || 0), 0) / exits.length
    : null;
  const totalUnrealizedPnl = openPositions.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Trade History</div>
          <div className="page-subtitle">All purchases, sales, and open positions</div>
        </div>
      </div>

      <div className="page-body section-gap">

        {/* Summary Stats */}
        <div className="stat-grid stat-grid-4">
          <div className="stat-card">
            <div className="stat-label">Total Realized P&L</div>
            <div className={`stat-value ${colorClass(totalRealizedPnl)}`}>{fmtMoney(totalRealizedPnl)}</div>
            <div className="stat-sub">{exits.length} closed position{exits.length !== 1 ? 's' : ''}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Unrealized P&L</div>
            <div className={`stat-value ${colorClass(totalUnrealizedPnl)}`}>{fmtMoney(totalUnrealizedPnl)}</div>
            <div className="stat-sub">{openPositions.length} open position{openPositions.length !== 1 ? 's' : ''}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Win Rate</div>
            <div className={`stat-value ${winRate != null ? (winRate >= 50 ? 'positive' : 'negative') : ''}`}>
              {winRate != null ? `${winRate.toFixed(0)}%` : '—'}
            </div>
            <div className="stat-sub">{winCount} wins / {exits.length - winCount} losses</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Avg Return per Exit</div>
            <div className={`stat-value ${colorClass(avgReturn)}`}>
              {avgReturn != null ? fmtPct(avgReturn) : '—'}
            </div>
            <div className="stat-sub">{allRows.length} total records</div>
          </div>
        </div>

        <SleevePicker sleeves={sleeves} activeId={activeSleeve} onChange={setActiveSleeve} />

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="tabs">
            {[['all','All'],['entry','Entries'],['exit','Exits'],['open','Open']].map(([v,l]) => (
              <button key={v} className={`tab ${filter===v?'active':''}`} onClick={()=>setFilter(v)}>{l}</button>
            ))}
          </div>
          <input
            className="form-input"
            style={{ width: 220, padding: '6px 12px', fontSize: 12 }}
            placeholder="Search ticker / CUSIP / name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span style={{ fontSize: 11, color: 'var(--text-lo)', fontFamily: 'var(--font-mono)', marginLeft: 'auto' }}>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Trade Table */}
        <div className="card">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">◇</div>
              <div className="empty-state-title">No records found</div>
              <div className="empty-state-desc">Entries appear when positions are added, exits when they are closed.</div>
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Ticker</th>
                    <th>Security</th>
                    <th>Sleeve</th>
                    <th>Dir</th>
                    <th style={{textAlign:'right'}}>Qty</th>
                    <th style={{textAlign:'right'}}>Price</th>
                    <th style={{textAlign:'right'}}>Trade Value</th>
                    <th style={{textAlign:'right'}}>Entry Price</th>
                    <th style={{textAlign:'right'}}>Realized P&L</th>
                    <th style={{textAlign:'right'}}>Unr. P&L</th>
                    <th style={{textAlign:'right'}}>Return</th>
                    <th>Notes</th>
                    {isOwner && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(t => (
                    <tr key={t.id} style={t.is_open ? {background:'rgba(90,90,110,0.06)'} : {}}>
                      <td>
                        <div className="primary" style={{fontSize:12}}>{fmt(t.traded_at)}</div>
                        <div style={{fontSize:10,color:'var(--text-lo)',fontFamily:'var(--font-mono)',marginTop:1}}>{fmtTime(t.traded_at)}</div>
                      </td>
                      <td>
                        {t.trade_type === 'exit' ? (
                          <span className="badge badge-short">▼ Exit</span>
                        ) : t.is_open ? (
                          <span className="badge" style={{background:'rgba(90,90,110,0.25)',color:'var(--text-mid)',border:'1px solid var(--border)'}}>● Open</span>
                        ) : (
                          <span className="badge badge-long">▲ Entry</span>
                        )}
                      </td>
                      <td className="primary">{t.ticker || '—'}</td>
                      <td style={{maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'var(--text-mid)',fontSize:11}}>
                        {t.security_name || t.cusip || '—'}
                      </td>
                      <td style={{color:'var(--text-mid)',fontSize:11}}>{t.sleeves?.name || '—'}</td>
                      <td><span className={`badge badge-${t.direction}`}>{t.direction}</span></td>
                      <td style={{textAlign:'right'}}>{fmtNum(t.quantity,0)}</td>
                      <td style={{textAlign:'right',fontFamily:'var(--font-mono)',fontSize:12}}>${fmtNum(t.price,2)}</td>
                      <td style={{textAlign:'right'}}>{fmtMoney(t.trade_value)}</td>
                      <td style={{textAlign:'right',color:'var(--text-lo)',fontFamily:'var(--font-mono)',fontSize:11}}>
                        {t.trade_type==='exit' && t.entry_price ? `$${fmtNum(t.entry_price,2)}` : '—'}
                      </td>
                      <td style={{textAlign:'right'}} className={t.trade_type==='exit' ? colorClass(t.realized_pnl) : ''}>
                        {t.trade_type==='exit' ? fmtMoney(t.realized_pnl) : '—'}
                      </td>
                      <td style={{textAlign:'right'}} className={t.is_open ? colorClass(t.unrealized_pnl) : ''}>
                        {t.is_open ? fmtMoney(t.unrealized_pnl) : '—'}
                      </td>
                      <td style={{textAlign:'right'}} className={colorClass(t.trade_type==='exit' ? t.realized_pnl_pct : t.is_open ? t.unrealized_pnl_pct : null)}>
                        {t.trade_type==='exit' && t.realized_pnl_pct!=null
                          ? fmtPct(t.realized_pnl_pct)
                          : t.is_open && t.unrealized_pnl_pct!=null
                            ? fmtPct(t.unrealized_pnl_pct)
                            : '—'}
                      </td>
                      <td style={{fontSize:11,color:'var(--text-lo)',maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {t.notes || '—'}
                      </td>
                      {isOwner && (
                        <td style={{textAlign:'center'}}>
                          <button
                            onClick={() => setDeleteTarget({ row: t })}
                            style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-lo)',fontSize:14,padding:'2px 6px',borderRadius:'var(--radius-sm)'}}
                            title="Delete — wipes from all dashboard calculations"
                          >✕</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirm modal */}
      {deleteTarget?.row && (
        <div className="modal-backdrop">
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-header">
              <span className="modal-title">Delete Trade Record</span>
              <button className="btn btn-icon btn-secondary" onClick={()=>setDeleteTarget(null)} style={{padding:'4px 8px',fontSize:14}}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{color:'var(--text-secondary)',fontSize:13,marginBottom:16}}>
                Delete all record of this trade for {deleteTarget.row?.ticker || deleteTarget.row?.cusip || '—'}?
                This scrubs it from history and removes its impact from all P&L and dashboard calculations.
              </p>
              {deleteTarget.row?.trade_type === 'exit' && deleteTarget.row?.realized_pnl != null && (
                <div style={{background:'rgba(160,24,24,0.08)',border:'1px solid rgba(160,24,24,0.25)',borderRadius:'var(--radius-sm)',padding:'10px 14px',fontSize:12,marginBottom:12}}>
                  <strong style={{color:'var(--garnet-pale)'}}>
                    ⚠ Removes {deleteTarget.row.realized_pnl >= 0 ? '+' : ''}{fmtMoney(deleteTarget.row.realized_pnl)} from realized P&L.
                  </strong>
                  <div style={{color:'var(--text-secondary)',marginTop:4}}>Win rate and P&L totals update automatically.</div>
                </div>
              )}
              <div style={{background:'rgba(60,60,60,0.12)',borderRadius:'var(--radius-sm)',padding:'10px 14px',fontSize:12,color:'var(--text-lo)'}}>
                This cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setDeleteTarget(null)}>Cancel</button>
              <button
                className="btn"
                style={{background:'var(--red)',color:'#fff',border:'none'}}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Record'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
